import { getApiErrorDetail, useApiClient } from '@klareospace/pharos-lib-front-utils';
import type { CreateHandlingUnitType, HandlingUnitType } from '@klareospace/pharos-lib-types';
import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { createHandlingUnitTypeApi, handlingUnitTypeKeys } from '../api';

export function useCreateHandlingUnitType(): UseMutationResult<HandlingUnitType, Error, CreateHandlingUnitType> {
  const client = useApiClient('admin-auth');
  const api = createHandlingUnitTypeApi(client);
  const queryClient = useQueryClient();
  const { t } = useTranslation('settings');

  return useMutation<HandlingUnitType, Error, CreateHandlingUnitType>({
    mutationFn: (data: CreateHandlingUnitType) => api.createHandlingUnitType(data),
    onError: (error: Error) => {
      const detail = getApiErrorDetail(error);
      if (detail.errorCode === 'Conflict.DuplicateHandlingUnitTypeCode') {
        toast.error(t('handlingUnitTypes.toast.errorDuplicateCode'));
      } else if (detail.errorCode === 'Conflict.DuplicateHandlingUnitTypeLabel') {
        toast.error(t('handlingUnitTypes.toast.errorDuplicateLabel'));
      } else {
        toast.error(detail.message || t('handlingUnitTypes.toast.createError'));
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: handlingUnitTypeKeys.lists() });
      toast.success(t('handlingUnitTypes.toast.created'));
    },
  });
}
